/* eslint-disable max-lines */
import { useForm } from 'react-hook-form';
import { UpdateReleaseVersionSongPatchData } from 'types/releases';
import { SongDDL } from 'types/song';
import { ddlToOptions, getValidDirtyValues } from 'app/utils/utils';
import { useAppDispatch } from 'app/store';
import { updateLabelVersionEffect } from 'app/store/Releases/effects';
import { useEffect, useMemo } from 'react';
import { getOptions } from 'app/components/Song/Details/SongDetailsForm/utils';
import { getKeySignature } from '../LabelVersionDetailsForm/hooks';
import { getFirstDefaultGenre } from '../TuneCoreVersionDetailsForm/utils';
import {
  convertFormDataToGenreOperationsData,
  getLanguage,
  getReleaseSongVersionProperty,
} from '../utils';
import {
  convertSongVersionFormToApiRequestData,
  getAutocompleteFallback,
  getFieldFallback,
} from '../LabelVersionDetailsForm/utils';
import {
  FallbackAutocompleteField,
  FallbackTextField,
} from '../LabelVersionDetailsForm/types';
import { TuneCoreVersionDetailsFormData } from '../TuneCoreVersionDetailsForm/types';
import {
  LabelV2VersionDetailsFormComponentProps,
  LabelV2VersionDetailsFormData,
} from './types';
import { ReleaseErrorMsg } from '../ReleaseErrorMsg';

export const useLabelV2VersionDetails = ({
  releaseVersion,
  languageCodes,
  songDDL = {} as SongDDL,
  versionSongValidations,
  updateVersion,
  updateAudioValidations,
  primaryAudio,
  fetchAudioDetailsValidations,
  isAudioReplacing,
  isSyncing,
  primaryAudioFetching,
  replaceAudioVersion,
  updateAudioDetails,
}: LabelV2VersionDetailsFormComponentProps) => {
  const dispatch = useAppDispatch();

  const song = releaseVersion?.song;
  const timeSignature = getReleaseSongVersionProperty(song, 'timeSignature');
  const languages = getOptions(versionSongValidations.language, languageCodes);

  const primaryAudioDisabled =
    isAudioReplacing || isSyncing || primaryAudioFetching;

  const isrcDisabled =
    primaryAudioDisabled ||
    Boolean(primaryAudio?.audioData?.isIsrcAllocatedBySession);

  const genres = useMemo(() => ddlToOptions(songDDL.genres, true), [
    songDDL.genres,
  ]);

  const keySignatures = getOptions(
    versionSongValidations.keySignature,
    songDDL?.keySignatures || {}
  );

  useEffect(() => {
    if (!updateAudioValidations && primaryAudio?.audioData.guid) {
      fetchAudioDetailsValidations(primaryAudio.audioData.guid);
    }
  }, [updateAudioValidations, primaryAudio, fetchAudioDetailsValidations]);

  const defaultValues: LabelV2VersionDetailsFormData = {
    altTitle:
      releaseVersion?.song.overrides?.altTitle ||
      releaseVersion.metadata.versionTitle ||
      '',
    displayArtistName: getReleaseSongVersionProperty(song, 'displayArtistName'),
    keySignature: getKeySignature(
      songDDL,
      getReleaseSongVersionProperty(song, 'keySignature')
    ),
    timeSignature: timeSignature
      ? { title: timeSignature, value: timeSignature }
      : null,
    tempo: getReleaseSongVersionProperty(song, 'tempo'),
    duration: getReleaseSongVersionProperty(song, 'duration'),
    language: getLanguage(
      languageCodes,
      getReleaseSongVersionProperty(song, 'language')
    ),
    explicit: song.overrides.explicit ?? (song.snapshot.explicit || false),
    location: getReleaseSongVersionProperty(song, 'location.address'),
    genre: getFirstDefaultGenre(song.snapshot.genres || [], songDDL.genres),
    excludeLyrics: releaseVersion.lyrics?.isExcluded || false,
  };

  const form = useForm<LabelV2VersionDetailsFormData>({
    defaultValues,
  });

  const {
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors, dirtyFields },
  } = form;

  const genre = watch('genre');
  const artist = watch('displayArtistName');

  const updateSongVersion = async (
    formData: UpdateReleaseVersionSongPatchData
  ) => {
    try {
      await dispatch(updateLabelVersionEffect({ formData })).unwrap();
    } catch {
      // proceed
    }
  };

  const onSubmit = async () => {
    const data = getValues();
    const validDirtyValues = getValidDirtyValues<LabelV2VersionDetailsFormData>(
      errors,
      dirtyFields,
      data
    );

    if (Object.keys(validDirtyValues).length) {
      reset(data, { keepErrors: true });
      await updateSongVersion(
        convertSongVersionFormToApiRequestData(
          validDirtyValues,
          releaseVersion,
          songDDL?.genres || {}
        )
      );
    }
  };

  const fallbackAutocompleteFieldToSnapshot: FallbackAutocompleteField = (
    name,
    options
  ) => (event) => {
    const { value, fallback } = getAutocompleteFallback(
      name,
      options,
      releaseVersion,
      event
    );

    if (value === '' && fallback) {
      setValue(name, fallback);
    }
    onSubmit();
  };

  const fallbackTextFieldToSnapshot: FallbackTextField = (name) => (event) => {
    if (event.target.value === '') {
      setValue(name, getFieldFallback(name, releaseVersion));
    }
    onSubmit();
  };

  const onGenreChange = (value) => {
    if (value === null) {
      return;
    }
    const genresData = convertFormDataToGenreOperationsData(
      {
        genre: value,
      },
      genre,
      releaseVersion
    );
    if (genresData.genres && value?.value !== genre?.value) {
      updateVersion(genresData as Partial<TuneCoreVersionDetailsFormData>, {
        refetchVersion: true,
      });
    }
    setValue('genre', value);
  };

  const getErrorMessage = (key: 'artist' | 'genre') => {
    if (key === 'artist') {
      return {
        invalid: !artist,
        element: (
          <ReleaseErrorMsg invalid={!artist} focus="displayArtistName">
            Artist missing!
          </ReleaseErrorMsg>
        ),
      };
    }

    if (key === 'genre') {
      const invalidGenre = !!releaseVersion.validationErrors?.some(
        ({ errorCode }) => errorCode === 'INVALID_SONG_GENRE'
      );
      const invalid = invalidGenre;
      const errorType = invalidGenre ? 'invalid' : 'missing';

      return {
        invalid,
        element: (
          <ReleaseErrorMsg invalid={invalid} focus="song-genre">
            Genre {errorType}!
          </ReleaseErrorMsg>
        ),
      };
    }

    return null;
  };

  return {
    form,
    defaultValues,
    onSubmit,
    fallbackAutocompleteFieldToSnapshot,
    fallbackTextFieldToSnapshot,
    genres,
    keySignatures,
    languages,
    onGenreChange,
    isrcDisabled,
    replaceAudioVersion,
    updateAudioValidations,
    updateAudioDetails,
    getErrorMessage,
  };
};
